import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { ComponentCard, Component } from "@/components/components/ComponentCard";
import { ComponentFilters } from "@/components/components/ComponentFilters";
import { toast } from "sonner";
import { LayoutGrid, Loader2 } from "lucide-react";

export default function Components() {
  const [filters, setFilters] = useState({
    search: "",
    mainCategory: "All",
    subCategory: "All",
    finish: "All",
    sort: "newest",
  });

  // Fetch from database
  const { data: dbComponents, isLoading } = useQuery({
    queryKey: ["public-components"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("components")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Map DB rows to Component interface
  const components: Component[] = useMemo(() => {
    if (!dbComponents) return [];
    return dbComponents.map((c) => ({
      id: c.id,
      name: c.name,
      mainCategory: c.main_category,
      subCategory: c.sub_category,
      image: c.image_url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=600&fit=crop",
      colors: c.colors || [],
      finish: c.finish || "",
    }));
  }, [dbComponents]);

  const filteredComponents = useMemo(() => {
    let result = [...components];

    if (filters.search) {
      const query = filters.search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.mainCategory.toLowerCase().includes(query) ||
          c.subCategory.toLowerCase().includes(query)
      );
    }

    if (filters.mainCategory !== "All") {
      result = result.filter((c) => c.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory !== "All") {
      result = result.filter((c) => c.subCategory === filters.subCategory);
    }
    if (filters.finish !== "All") {
      result = result.filter((c) => c.finish === filters.finish);
    }
    switch (filters.sort) {
      case "name": result.sort((a, b) => a.name.localeCompare(b.name)); break;
    }

    return result;
  }, [components, filters]);

  const handleFavorite = (id: string) => {
    toast.success("Favorite toggled");
  };

  const handleAddToProject = (id: string) => {
    toast.success("Component added to project");
  };

  const handleComponentClick = (component: Component) => {
    toast.info(`Viewing ${component.name}`);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 pt-24 pb-8 lg:pt-28 lg:pb-12">
        <div className="mb-8">
          <h1 className="mb-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            Component Library
          </h1>
          <p className="text-lg text-muted-foreground">
            Discover premium architectural components and design elements from trusted suppliers worldwide
          </p>
        </div>

        <div className="mb-8">
          <ComponentFilters
            activeFilters={filters}
            onSearch={(query) => setFilters((f) => ({ ...f, search: query }))}
            onMainCategoryChange={(mainCategory) => setFilters((f) => ({ ...f, mainCategory }))}
            onSubCategoryChange={(subCategory) => setFilters((f) => ({ ...f, subCategory }))}
            onFinishChange={(finish) => setFilters((f) => ({ ...f, finish }))}
            onSortChange={(sort) => setFilters((f) => ({ ...f, sort }))}
          />
        </div>

        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredComponents.length} of {components.length} components
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : filteredComponents.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredComponents.map((component) => (
              <ComponentCard
                key={component.id}
                component={component}
                onFavorite={handleFavorite}
                onAddToProject={handleAddToProject}
                onClick={handleComponentClick}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-4 rounded-full bg-muted p-4">
              <LayoutGrid className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mb-2 font-serif text-xl font-medium text-foreground">No components found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
